import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from './ui/use-toast';
import { Copy, Check, RefreshCw, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from './ui/alert-dialog';
import { profileService } from '../services/profile';
import type { User } from '../types/user';

interface CoachInviteLinkProps {
  token: string;
  /** Receives the refreshed profile after the link is regenerated. */
  onRegenerated: (user: User) => void;
}

/**
 * Shows the coach's secret shareable invite link with copy/share and a
 * regenerate action (which invalidates the previously shared link).
 */
export function CoachInviteLink({ token, onRegenerated }: CoachInviteLinkProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const link = `${window.location.origin}/coach/${token}`;

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: t('profile.coach.link.shareTitle'), url: link });
        return;
      }
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ description: t('profile.coach.link.copied') });
    } catch {
      // User dismissed the share sheet or clipboard is unavailable — ignore.
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await profileService.regenerateCoachToken();
      onRegenerated(response.user);
      toast({ description: t('profile.coach.link.regenerated') });
    } catch {
      toast({ variant: 'destructive', description: t('profile.coach.link.regenerateError') });
    } finally {
      setRegenerating(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('profile.coach.link.title')}</CardTitle>
        <CardDescription>{t('profile.coach.link.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Input value={link} readOnly onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
          <Button type="button" variant="secondary" size="icon" onClick={handleShare} title={t('profile.coach.link.copy')}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start text-muted-foreground"
          onClick={() => setConfirmOpen(true)}
          disabled={regenerating}
        >
          {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {t('profile.coach.link.regenerate')}
        </Button>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('profile.coach.link.regenerateConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('profile.coach.link.regenerateConfirmBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRegenerate();
              }}
            >
              {t('profile.coach.link.regenerate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
